import React, { ReactNode, useState, useEffect } from 'react'
import styled from 'styled-components'
import { Label } from '../Attribute'
import Popover, { PopoverChildrenProps } from 'components/Popover'
import { Color as ColorType, ColorVariable } from 'modules/shares/types'
import Color, { ColorIcon } from '../Attribute/components/Color'
import Tooltip from 'components/Tooltip'
import useHandleCopyValue from 'utils/hooks/useHandleCopyValue'
import { ColorFormat } from '../Attribute/components/Color/utils'
import { truncateStyles } from 'components/Truncate'
import { Box } from 'components/Box'
import { formatPath } from 'modules/shares/Inspector/Sidebar/utils'

interface ColorVariablePopoverProps extends ColorType {
  toggle: PopoverChildrenProps
  visible: boolean
  colorVariable: ColorVariable
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  hasScrollBar?: boolean
  onClickOutside?: () => void
  colorAttribute: ReactNode
  dropdown: ReactNode
}

const ClickableArea = styled(Label)`
  cursor: pointer;
`

const ColorVariableContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border.dark.extraHigh};
  border-radius: ${({ theme }) => theme.radii.base};
  padding: 12px 8px;
  line-height: 1;
  box-sizing: border-box;
  cursor: pointer;
  max-width: 180px;
`

const ColorName = styled.div<{ hasGroups: boolean }>`
  word-break: word;
  max-height: ${({ theme, hasGroups }) =>
    hasGroups ? theme.fontSizes.smaller : theme.fontSizes.xlarge};
  ${truncateStyles};
`

const ColorInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  white-space: nowrap;
  overflow: hidden;
`

const ColorGroup = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  overflow: hidden;
  text-overflow: ellipsis;
`

const Container = styled.div`
  padding: 16px 12px;
  min-width: 272px;
  max-width: 320px;
  line-height: 1.25;
`

const SourceLibraryName = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  font-size: ${({ theme }) => theme.fontSizes.tiny};
  word-break: break-all;
`

const ColorVariableName = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.primary};
  font-size: ${({ theme }) => theme.fontSizes.tiny};
  word-break: break-all;
  margin-top: 4px;
`

const ColorAttributeContainer = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.border.dark.base};
  padding: 12px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
`

const CopiableValue: React.FC<{ value: string }> = ({ value, children }) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(value)

  return (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={copyTooltipText}>
      <ClickableArea
        onClick={handleCopyValue}
        onMouseEnter={handleEnterCopiableArea}
        onMouseLeave={handleLeaveCopiableArea}>
        {children}
      </ClickableArea>
    </Tooltip>
  )
}

const ColorVariablePopoverWrapper: React.FC<ColorVariablePopoverProps> = ({
  red,
  green,
  blue,
  alpha,
  toggle,
  visible,
  colorVariable,
  onColorFormatChange,
  colorFormat,
  hasScrollBar,
  onClickOutside,
  colorAttribute,
  dropdown,
  ...props
}) => {
  const groups = colorVariable.name.split('/')

  const variableName =
    groups.length > 0 ? groups[groups.length - 1] : colorVariable.name

  groups.splice(-1, 1)

  const sourceLibrary = colorVariable.sourceLibraryName
    ? colorVariable.sourceLibraryName.trim()
    : 'This Document'

  const colorPath =
    groups.length !== 0
      ? `${sourceLibrary}/${formatPath(groups)}`
      : sourceLibrary

  return (
    <Popover
      visible={visible}
      placement="top"
      onClickOutside={onClickOutside}
      popup={
        <>
          <Container>
            <CopiableValue value={colorPath}>
              <SourceLibraryName>{colorPath}</SourceLibraryName>
            </CopiableValue>
            <CopiableValue value={variableName}>
              <ColorVariableName>{variableName}</ColorVariableName>
            </CopiableValue>
          </Container>
          <ColorAttributeContainer>
            {colorAttribute}
            <Box marginLeft={1}>{dropdown}</Box>
          </ColorAttributeContainer>
        </>
      }
      spacing="18px"
      data-testid="color-variable-popover"
      {...props}>
      {toggle}
    </Popover>
  )
}

export const ColorVariablePopover: React.FC<{
  red: number
  green: number
  blue: number
  alpha: number
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  copyValue: string
  colorVariable: ColorVariable
  dropdown: ReactNode
}> = ({
  red,
  green,
  blue,
  alpha = 1,
  onColorFormatChange,
  colorFormat,
  copyValue,
  colorVariable,
  dropdown,
}) => {
  const [isPopoverOpen, setPopoverOpen] = useState(false)
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  // Every time the color variable changes the popover should hide
  useEffect(() => {
    setPopoverOpen(false)
  }, [colorVariable])

  const groups = colorVariable.name.split('/')

  const name =
    groups.length > 0 ? groups[groups.length - 1] : colorVariable.name

  const hasGroups = groups.length > 1

  // remove the color name from the groups array
  groups.splice(-1, 1)

  const colorAttribute = (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={copyTooltipText}>
      <ClickableArea
        onClick={handleCopyValue}
        onMouseEnter={handleEnterCopiableArea}
        onMouseLeave={handleLeaveCopiableArea}>
        <Color
          red={red}
          green={green}
          blue={blue}
          alpha={alpha}
          format={colorFormat}
          maxTextWidth={160}
        />
      </ClickableArea>
    </Tooltip>
  )

  return (
    <ColorVariablePopoverWrapper
      red={red}
      green={green}
      blue={blue}
      alpha={alpha}
      visible={isPopoverOpen}
      colorVariable={colorVariable}
      onColorFormatChange={onColorFormatChange}
      colorFormat={colorFormat}
      onClickOutside={() => setPopoverOpen(false)}
      dropdown={dropdown}
      toggle={
        <ColorVariableContainer onClick={() => setPopoverOpen(true)}>
          <ColorIcon
            red={red}
            green={green}
            blue={blue}
            alpha={alpha}
            iconSize={24}
          />
          <ColorInfo>
            <ColorName hasGroups={hasGroups}>{name}</ColorName>
            {hasGroups && <ColorGroup>{formatPath(groups)}</ColorGroup>}
          </ColorInfo>
        </ColorVariableContainer>
      }
      colorAttribute={colorAttribute}
    />
  )
}

export default ColorVariablePopover
